import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import RecoveryCodeModal from "../components/RecoveryCodeModal";
import { useAuth } from "../context/AuthContext";
import { usePromoStatus } from "../hooks/usePromoStatus";
import { formatApiErrorDetail } from "../lib/api";
import { formatDateShort } from "../lib/currency";

export default function SignupAgency() {
  const { signupAgency } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const promo = usePromoStatus();

  const [form, setForm] = useState({
    agency_name: "",
    contact_name: "",
    email: "",
    password: "",
    website: "",
    siret: "",
    application_notes: "",
  });
  const [specialties, setSpecialties] = useState([]);
  const [specialtyInput, setSpecialtyInput] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addSpecialty = (raw) => {
    const value = (raw || "").replace(/^[,\s]+|[,\s]+$/g, "").trim();
    if (!value) return;
    setSpecialties((s) => (s.includes(value) ? s : [...s, value]));
    setSpecialtyInput("");
  };

  const onSpecialtyKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addSpecialty(specialtyInput);
    } else if (e.key === "Backspace" && !specialtyInput && specialties.length) {
      setSpecialties((s) => s.slice(0, -1));
    }
  };

  const removeSpecialty = (tag) =>
    setSpecialties((s) => s.filter((x) => x !== tag));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const data = await signupAgency({ ...form, specialties });
      if (data?.recovery_code) {
        setRecoveryCode(data.recovery_code);
      } else {
        setSubmitted(true);
      }
    } catch (err) {
      setError(
        formatApiErrorDetail(err.response?.data?.detail) ||
          t("common.error_generic")
      );
    } finally {
      setSubmitting(false);
    }
  };

  const finishAfterModal = () => {
    setRecoveryCode(null);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="signup-agency-success">
        <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6 py-12">
          <Link to="/" className="mb-6 flex items-center gap-2 text-slate-900">
            <img src="/Logo.png" alt="Talent Junction" className="h-14 w-14 rounded-xl object-cover" />
            <span className="text-base font-semibold tracking-tight">{t("brand")}</span>
          </Link>
          <div className="w-full rounded-2xl border border-emerald-200 bg-white p-8 shadow-sm">
            <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
              {t("signup_agency_success.title")}
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              {t("signup_agency_success.body")}
            </p>
            {promo && promo.active && (
              <p className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="signup-agency-promo-note">
                <span className="mr-1">🌞</span>
                <strong>{t("promo.summer_label")}</strong> —{" "}
                {t("promo.signup_agency_note", { date: formatDateShort(promo.end_date) })}
              </p>
            )}
            <button
              onClick={() => navigate("/dashboard")}
              className="mt-6 inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              data-testid="signup-agency-success-cta"
            >
              {t("signup_agency_success.cta")}
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="signup-agency-page">
      <main className="mx-auto flex max-w-xl flex-col px-6 py-12">
        <Link to="/" className="mb-6 flex items-center gap-2 self-center text-slate-900">
          <img src="/Logo.png" alt="Talent Junction" className="h-14 w-14 rounded-xl object-cover" />
          <span className="text-base font-semibold tracking-tight">{t("brand")}</span>
        </Link>
        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            {t("auth.agency_title")}
          </h1>
          <p className="mt-2 text-sm text-slate-600">{t("auth.agency_subtitle")}</p>

          <form onSubmit={onSubmit} className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="signup-agency-form">
            <Field label={t("auth.agency_name")} value={form.agency_name} onChange={set("agency_name")} testId="signup-agency-name-input" required className="sm:col-span-2" />
            <Field label={t("auth.contact_name")} value={form.contact_name} onChange={set("contact_name")} testId="signup-agency-contact-input" required />
            <Field label={t("auth.email")} type="email" value={form.email} onChange={set("email")} testId="signup-agency-email-input" required />
            <Field label={t("auth.password")} type="password" value={form.password} onChange={set("password")} testId="signup-agency-password-input" required minLength={8} />
            <Field label={t("auth.siret")} value={form.siret} onChange={set("siret")} testId="signup-agency-siret-input" />
            <Field label={t("auth.website")} value={form.website} onChange={set("website")} testId="signup-agency-website-input" className="sm:col-span-2" placeholder="https://" />

            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm font-medium text-slate-700">
                {t("auth.specialties")}
              </label>
              <div
                className="flex flex-wrap items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2 py-1.5 shadow-sm focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/20"
                data-testid="signup-agency-specialty-container"
              >
                {specialties.map((s) => (
                  <span
                    key={s}
                    className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700"
                    data-testid={`specialty-tag-${s}`}
                  >
                    {s}
                    <button
                      type="button"
                      onClick={() => removeSpecialty(s)}
                      className="text-indigo-500 hover:text-indigo-700"
                      aria-label={`remove ${s}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  value={specialtyInput}
                  onChange={(e) => setSpecialtyInput(e.target.value)}
                  onKeyDown={onSpecialtyKey}
                  onBlur={() => addSpecialty(specialtyInput)}
                  placeholder={t("auth.specialty_placeholder")}
                  className="min-w-[10ch] flex-1 border-0 bg-transparent px-1 py-0.5 text-sm text-slate-900 focus:outline-none focus:ring-0"
                  data-testid="signup-agency-specialty-input"
                />
              </div>
              <p className="mt-1 text-xs text-slate-500">{t("auth.specialties_hint")}</p>
            </div>

            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm font-medium text-slate-700">
                {t("auth.application_notes")}
              </label>
              <textarea
                value={form.application_notes}
                onChange={set("application_notes")}
                rows={4}
                placeholder={t("auth.application_notes_placeholder")}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                data-testid="signup-agency-notes-input"
              />
            </div>

            {error && (
              <div
                className="sm:col-span-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
                data-testid="signup-agency-error"
              >
                {error}
              </div>
            )}

            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                data-testid="signup-agency-submit-btn"
              >
                {submitting ? t("common.loading") : t("auth.submit_signup_agency")}
              </button>
              <p className="mt-3 text-xs text-slate-500" data-testid="signup-agency-cgv-note">
                {t("legal.signup_accept")}
                <Link to="/legal/cgv" className="font-medium text-indigo-700 hover:text-indigo-800">
                  {t("legal.signup_accept_cgv")}
                </Link>
                .
              </p>
            </div>
          </form>

          <p className="mt-6 text-sm text-slate-600">
            {t("auth.already_have")}{" "}
            <Link to="/login" className="font-medium text-indigo-700 hover:text-indigo-800">
              {t("auth.login_link")}
            </Link>
          </p>
        </div>
      </main>

      {recoveryCode && (
        <RecoveryCodeModal code={recoveryCode} variant="signup" onClose={finishAfterModal} />
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId, required, minLength, className = "", placeholder }) {
  return (
    <div className={className}>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
      <input
        type={type}
        required={required}
        minLength={minLength}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
        data-testid={testId}
      />
    </div>
  );
}
